// The menu section already renders live, available products in Shopify's
// best-selling order with market prices and the theme's native quick-add forms.
class FibriyaEmptyCart extends HTMLElement {
  connectedCallback() {
    this.cancel();
    this.products = this.querySelector('.fec-products');
    // Section updates may preserve this custom element while replacing its
    // children. Rebind to the visible container instead of writing to old DOM.
    this.contentObserver = new MutationObserver(() => this.reconcile());
    this.contentObserver.observe(this, { childList: true, subtree: true });
    if (this.hasAttribute('data-drawer')) {
      const dialog = this.closest('dialog');
      if (dialog) {
        this.openObserver = new MutationObserver(() => { if (dialog.open) this.load(); });
        this.openObserver.observe(dialog, { attributes: true, attributeFilter: ['open'] });
        if (dialog.open) this.load();
      }
    } else {
      this.load();
    }
  }

  disconnectedCallback() { this.cancel(); }

  reconcile() {
    const products = this.querySelector('.fec-products');
    if (!products) return;
    if (products !== this.products) {
      this.cancelLoad();
      this.products = products;
    }
    const visible = !this.hasAttribute('data-drawer') || this.closest('dialog')?.open;
    if (visible && !this.loading && !products.querySelector('.fec-product, button')) this.load();
  }

  cancel() {
    this.cancelLoad();
    this.openObserver?.disconnect();
    this.contentObserver?.disconnect();
  }

  cancelLoad() {
    this.version = (this.version || 0) + 1;
    this.request?.abort();
    clearTimeout(this.timeout);
    this.loading = false;
    this.products?.setAttribute('aria-busy', 'false');
  }

  async load() {
    if (this.loading || !this.products) return;
    this.loading = true;
    this.products.setAttribute('aria-busy', 'true');
    const version = this.version = (this.version || 0) + 1;
    const request = this.request = new AbortController();
    let timedOut = false;
    const timeout = this.timeout = setTimeout(() => { timedOut = true; request.abort(); }, 15000);
    const status = document.createElement('p');
    status.className = 'fec-load-status';
    status.setAttribute('role', 'status');
    status.textContent = this.dataset.loading;
    if (!this.products.querySelector('.fec-product')) this.products.replaceChildren(status);
    try {
      let next = (window.Shopify?.routes?.root || '/') + 'collections/all';
      const visited = new Set(), ids = new Set(), cards = [];
      while (next && cards.length < 4) {
        const url = new URL(next, location.origin);
        if (url.origin !== location.origin || visited.has(url.pathname + url.search)) throw Error('Invalid bestseller page');
        visited.add(url.pathname + url.search);
        url.searchParams.set('sort_by', 'best-selling');
        url.searchParams.set('section_id', 'fibriya-menu-bestsellers');
        const response = await fetch(url, { credentials: 'same-origin', signal: request.signal });
        if (!response.ok) throw Error('Bestsellers unavailable');
        const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
        const page = doc.querySelector('[data-fm-bestseller-page]');
        if (!page || page.dataset.sort !== 'best-selling' || page.dataset.collection !== 'all') throw Error('Invalid bestseller order');
        for (const source of page.querySelectorAll('.fm-product[data-available="true"]')) {
          if (ids.has(source.dataset.productId)) continue;
          ids.add(source.dataset.productId);
          const card = document.importNode(source, true);
          for (const [from, to] of [['fm-product', 'fec-product'], ['fm-product-image', 'fec-product-image'], ['fm-product-title', 'fec-product-title'], ['fm-price', 'fec-price']]) {
            if (card.classList.contains(from)) card.classList.replace(from, to);
            for (const node of card.querySelectorAll('.' + from)) node.classList.replace(from, to);
          }
          // The same product can also occur in the mobile menu. Keep form IDs unique.
          const renamed = new Map();
          const prefix = 'Fec-' + (this.dataset.instance || 'page') + '-';
          for (const node of card.querySelectorAll('[id]')) {
            renamed.set(node.id, prefix + node.id);
            node.id = prefix + node.id;
          }
          for (const node of card.querySelectorAll('[form], [for], [aria-describedby], [aria-labelledby], [aria-controls]')) {
            for (const attr of ['form', 'for', 'aria-describedby', 'aria-labelledby', 'aria-controls']) {
              if (node.hasAttribute(attr)) node.setAttribute(attr, node.getAttribute(attr).split(' ').map(id => renamed.get(id) || id).join(' '));
            }
          }
          for (const button of card.querySelectorAll('.quick-add__button')) {
            const text = button.querySelector('.add-to-cart-text__content')?.textContent?.trim();
            if (text) button.setAttribute('aria-label', text + ' – ' + card.querySelector('.fec-product-title').textContent.trim());
          }
          cards.push(card);
          if (cards.length === 4) break;
        }
        next = page.dataset.next;
      }
      if (!cards.length) throw Error('No available bestseller products');
      if (version !== this.version || !this.isConnected) return;
      this.products.replaceChildren(...cards);
    } catch (error) {
      if (version !== this.version || !this.isConnected || (error.name === 'AbortError' && !timedOut)) return;
      status.textContent = '';
      const retry = document.createElement('button');
      retry.type = 'button';
      retry.textContent = this.dataset.retry;
      retry.addEventListener('click', () => this.load());
      status.append(retry);
      this.products.replaceChildren(status);
      console.warn('Empty cart bestsellers could not be loaded', timedOut ? 'timeout' : error.name);
    } finally {
      clearTimeout(timeout);
      if (version === this.version) {
        this.loading = false;
        this.products.setAttribute('aria-busy', 'false');
      }
    }
  }
}
if (!customElements.get('fibriya-empty-cart')) customElements.define('fibriya-empty-cart', FibriyaEmptyCart);
